import React, { useCallback, useState } from 'react';
import { formatPrice } from '@moda/portal-stanchions';
import {
  Button,
  ControlLink,
  Expandable,
  Input,
  LoadingBalls,
  Overlay,
  Stack,
  Text
} from '@moda/om';
import ExpandIcon from '@moda/icons/expand-16';
import { useCart } from '../../hooks/useCart';
import { useGiftCardMutation } from '../../hooks/useGiftCardMutation';
import { reportError } from '../../lib/reportError';

import './CheckoutGiftCardSection.scss';

const DIGITS = -4;

export const CheckoutGiftCardSection = () => {
  const { cart } = useCart();
  const { addGiftCard, removeGiftCard, loading } = useGiftCardMutation();
  const [errors, setErrors] = useState<{
    giftCardNumberMessage?: string;
    giftCardPinMessage?: string;
  }>({});

  const name = (
    <Stack space={3} direction="horizontal" alignItems="center">
      <ExpandIcon />
      <Text treatment="bold1">ADD GIFT CARD</Text>
    </Stack>
  );

  const lastFourDigits = cart.attributes.giftCard?.cardNumber
    ? cart.attributes.giftCard.cardNumber.slice(DIGITS)
    : '';

  const validateForm = (giftCardNumber: string, giftCardPin: number) => {
    const validationErrors: { giftCardNumberMessage?: string; giftCardPinMessage?: string } = {};
    if (!giftCardNumber) {
      validationErrors.giftCardNumberMessage = 'Please enter your gift card number.';
    }
    if (!giftCardPin) {
      validationErrors.giftCardPinMessage = 'Please enter your PIN number.';
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const formData = new FormData(event.target as HTMLFormElement);
      const giftCardNumber = formData.get('giftCardNumber') as string;
      const giftCardPin = parseInt(formData.get('giftCardPin') as string, 10);

      if (!validateForm(giftCardNumber, giftCardPin)) {
        return;
      }

      try {
        await addGiftCard(giftCardNumber, giftCardPin);
        setErrors({});
      } catch (error) {
        reportError('GIFT_CARD_SUBMIT', error);
      }
    },
    [addGiftCard]
  );

  const onRemove = useCallback(async () => {
    try {
      await removeGiftCard();
    } catch (error) {
      reportError('REMOVE_GIFT_CARD', error);
    }
  }, [removeGiftCard]);

  return (
    <div className="CheckoutGiftCardSection">
      {cart.attributes.giftCard?.cardNumber ? (
        <Stack
          className="CheckoutGiftCardSection__existing-card"
          space={1}
          direction="horizontal"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Text className="CheckoutGiftCardSection__card-number">
            GIFT CARD ENDING {lastFourDigits}
          </Text>
          <Text className="CheckoutGiftCardSection__card-balance">
            {formatPrice(cart.attributes.giftCard.currency, cart.attributes.giftCard.balance)}
          </Text>
          <Text className="CheckoutGiftCardSection__card-expiration">
            expires {cart.attributes.giftCard.expiryDate}
          </Text>
          <ControlLink data-testid="removeGiftCard" onClick={onRemove}>
            Remove
          </ControlLink>
        </Stack>
      ) : (
        <Expandable
          icon="chevron"
          name={name}
          border={false}
          className="CheckoutGiftCardSection__new-card"
        >
          {cart.attributes.giftCard?.giftCardError && (
            <Text data-testid="giftCardErrorText" color="code-red" treatment="body1">
              {cart.attributes.giftCard?.giftCardError}
            </Text>
          )}
          <form onSubmit={handleSubmit}>
            <div className="CheckoutGiftCardSection__form">
              <Input
                className="CheckoutGiftCardSection__input"
                name="giftCardNumber"
                label="Gift card number"
                error={errors.giftCardNumberMessage}
              />
              <Input
                className="CheckoutGiftCardSection__input CheckoutGiftCardSection__input-pin"
                name="giftCardPin"
                label="PIN*"
                error={errors.giftCardPinMessage}
              />
            </div>
            <div className="CheckoutGiftCardSection__button-container">
              <Button className="CheckoutGiftCardSection__continue-button" type="submit">
                CONTINUE
              </Button>
            </div>
          </form>
        </Expandable>
      )}

      <Overlay show={loading}>
        <LoadingBalls />
        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </div>
  );
};
